"use client";

import React, { useEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { CheckIcon, ChevronDown } from "lucide-react";

export function MultiSelect ({
  children,
  value = [],
  onValueChange,
  dropdownKey,
  openDropdownKey,
  setOpenDropdownKey
}) {
  const isOpen = dropdownKey === openDropdownKey;
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside (event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownKey(null);
      }
    }

    if (isOpen) {
      // Small delay to allow the click event to process first
      setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);

      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, setOpenDropdownKey]);

  const handleToggle = e => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen) {
      setOpenDropdownKey(null);
    } else {
      setOpenDropdownKey(dropdownKey);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {React.Children.map(children, child => {
        if (child.type === MultiSelectTrigger) {
          return React.cloneElement(child, {
            onClick: handleToggle,
            "aria-expanded": isOpen
          });
        }
        if (child.type === MultiSelectContent) {
          if (!isOpen) return null;

          const content = React.cloneElement(child, {
            onValueChange,
            selectedValues: value
          });

          return content;
        }
        return child;
      })}
    </div>
  );
}

export function MultiSelectTrigger ({ className, children, onClick }) {
  return (
    <button
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2  ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
        className
      )}
      onClick={onClick}
      type="button"
    >
      <span className="truncate">{children}</span>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </button>
  );
}

export function MultiSelectContent ({
  className,
  children,
  onValueChange,
  selectedValues = [],
  ...props
}) {
  const handleSelect = (value, e) => {
    // Stop the event from reaching the document click handler
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onValueChange(newValues);
  };

  const contentRef = useRef(null);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const stopPropagation = e => {
      e.stopPropagation();
    };

    content.addEventListener("mousedown", stopPropagation);
    return () => content.removeEventListener("mousedown", stopPropagation);
  }, []);

  return (
    <div
      ref={contentRef}
      className={cn(
        "absolute z-50 min-w-[8rem] rounded-md border bg-white text-popover-foreground shadow-md w-full mt-1",
        className
      )}
      onMouseDown={e => e.stopPropagation()}
      {...props}
    >
      <div className="p-1 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-muted-foreground hover:scrollbar-thumb-ring">
        {React.Children.map(children, child => {
          if (child.type === MultiSelectItem) {
            const value = child.props.value;
            return React.cloneElement(child, {
              value,
              selected: selectedValues.includes(value),
              onSelect: e => handleSelect(value, e)
            });
          }
          return child;
        })}
      </div>
    </div>
  );
}

export function MultiSelectItem ({
  className,
  children,
  selected,
  onSelect,
  ...props
}) {
  const handleSelect = e => {
    if (onSelect) {
      onSelect(e);
    }
  };

  return (
    <div
      className={cn(
        "relative flex items-center cursor-pointer select-none rounded-sm py-1.5 pl-8 pr-2 hover:bg-accent hover:text-accent-foreground",
        selected && "bg-gray-100",
        className
      )}
      role="option"
      aria-selected={selected}
      onMouseDown={e => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={handleSelect}
      {...props}
    >
      <span
        className={cn(
          "absolute left-2 h-4 w-4 rounded-sm border border-gray-300 flex items-center justify-center transition-colors",
          selected && "border-[#FF5533] bg-[#FF5533]"
        )}
      >
        {selected && <CheckIcon className="h-3 w-3 text-white absolute" />}
      </span>
      {children}
    </div>
  );
}
